import React, { useRef, useState } from "react";

import Map, {
  FullscreenControl,
  GeolocateControl,
  NavigationControl,
  Source,
  Layer,
  Popup,
} from "react-map-gl";

import "./mapStyles.css";

import useMediaQuery from "@mui/material/useMediaQuery";

import maplibregl from "maplibre-gl";
import "./geocoder.css";

import {
  boroughSource,
  catchmentPartnershipSource,
  giglOpenSpaceSource,
  sudsSitesSource,
  thamesCombinedSource,
  thamesSeperateSource,
  watercoursesSource,
} from "./sources";
import {
  boroughLayer,
  catchmentPartnershipLayer,
  giglOpenSpaceLayer,
  sudsSitesLayer,
  thamesCombinedLayer,
  thamesSeparateLayer,
  watercoursesLayer,
} from "./layers";
import GeocoderControl from "./GeocoderControl";
import FullScreenDialog from "../components/FullScreenDialog";
import SudCard from "../components/SudCard";
import { CatchmentCard } from "../components/CatchmentCard";
import {
  highlightedOpacity,
  opacity,
  thamesCombinedColour,
  thamesSeparateColour,
  watercoursesColour,
} from "../assets/colours";
import { SudHoverCard } from "../components/SudHoverCard";

const OS_KEY = "vmRzM4mAA1Ag0hkjGh1fhA2hNLEM6PYP";

export function SudsMap({
  checkedSites,
  filtersProperties,
  noFilters,
  catchmentPartnershipProperties,
  giglOpenSpaceProperties,
  thamesCombinedProperties,
  thamesSeperateProperties,
  watercoursesProperties,
  boroughProperties,
  mapProperties,
  catchmentOpacityOffset,
  giglOpacityOffset,
  thamesCombinedOpacityOffset,
  thamesSeparateOpacityOffset,
  watercoursesOpacityOffset,
}) {
  const mapRef = useRef();
  const map = mapRef?.current;
  const isLargeMobile = useMediaQuery("(max-width: 1050px)");

  const handleResize = () => {
    if (map) {
      setTimeout(() => {
        map.resize();
      }, 200);
    }
  };
  window.addEventListener("resize", handleResize);

  const [sudPopup, setSudPopup] = useState(false);
  const [sudHover, setSudHover] = useState(false);
  const [sudHoverFeature, setSudHoverFeature] = useState();
  const [sudFeature, setSudFeature] = useState();
  const [catchmentPopup, setCatchmentPopup] = useState(false);
  const [catchmentFeature, setCatchmentFeature] = useState();

  const isDarkMode = Object.values(mapProperties)[0].visible;

  const handleMouseClick = (e) => {
    setSudPopup(false);
    setCatchmentPopup(false);

    if (!map) {
      return;
    }

    if (map.getLayer("suds-sites")) {
      const features = map.queryRenderedFeatures(e.point, {
        layers: ["suds-sites"],
      });

      if (features.length > 0) {
        setSudFeature(features[0]);
        const coordinates = features[0].geometry.coordinates;
        map.resize();
        map.flyTo({
          center: [coordinates[0], coordinates[1]],
        });

        setSudPopup(true);
        return;
      }
    }
    if (map.getLayer("catchment-partnership")) {
      const catchmentFeatures = map.queryRenderedFeatures(e.point, {
        layers: ["catchment-partnership"],
      });
      if (catchmentFeatures.length > 0) {
        const { caba_partn: name, caba_websi: website } =
          catchmentFeatures[0].properties;

        setCatchmentFeature({ name, website, lngLat: e.lngLat });
        setCatchmentPopup(true);
        map.getCanvas().style.cursor = "pointer";
        return;
      }
    }
    map.getCanvas().style.cursor = "";
  };

  const handleMouseHover = (e) => {
    if (!map) {
      map.getCanvas().style.cursor = "";
      return;
    }

    if (map.getLayer("suds-sites")) {
      const features = map.queryRenderedFeatures(e.point, {
        layers: ["suds-sites"],
      });

      if (features.length > 0) {
        map.getCanvas().style.cursor = "pointer";
        setSudHoverFeature(features[0]);
        setSudHover(true);
        return;
      }
    }
    setSudHover(false);
    if (map.getLayer("catchment-partnership")) {
      const catchmentFeatures = map.queryRenderedFeatures(e.point, {
        layers: ["catchment-partnership"],
      });
      if (catchmentFeatures.length > 0) {
        map.getCanvas().style.cursor = "pointer";
        return;
      }
    }
    if (!sudPopup && !catchmentPopup) {
      map.getCanvas().style.cursor = "";
    }
  };

  const filtersToShow = Object.keys(filtersProperties).filter(
    (filterName) => filtersProperties[filterName].visible
  );

  let filterExpression;
  if (noFilters) {
    filterExpression = [true];
  } else if (filtersToShow.length < 1) {
    filterExpression = [false];
  } else {
    filterExpression = filtersToShow.map((filter) => [
      "==",
      ["get", `${filter}`],
      "Y",
    ]);
  }

  const filter = [
    "all",
    getFilterExpressionsFromSourceProperties(checkedSites),
    ["any", ...filterExpression],
  ];

  const handleMapMoveStart = () => {
    map.getCanvas().style.cursor = "";
  };

  const handleMapMoveEnd = () => {
    if (sudPopup || catchmentPopup) {
      map.getCanvas().style.cursor = "pointer";
    }
  };

  const sudVisibleForPopup =
    sudFeature?.properties?.objectid &&
    map?.querySourceFeatures("suds-sites", {
      sourceLayer: "suds-sites",
      filter: [
        "all",
        ["==", ["get", "objectid"], sudFeature.properties.objectid],
        filter,
      ],
    })?.length > 0;

  return (
    <Map
      id="map"
      initialViewState={{
        longitude: -0.1276,
        latitude: 51.5072,
        zoom: 10,
        width: "100%",
        height: "100%",
        maxBounds: [
          [-0.9520147334465985, 51.10040608049066],
          [1.035128830924135, 52.119059856815895],
        ],
        minZoom: 9.2,
        transformRequest: (url) => {
          if (!url.toLowerCase().includes("api.os")) return { url: url };
          if (!/[?&]key=/.test(url)) url += "?key=" + OS_KEY;
          return {
            url: url + "&srs=3857",
          };
        },
      }}
      reuseMaps
      ref={mapRef}
      mapStyle={isDarkMode ? "./os_dark.json" : "./os_light.json"}
      styleDiffing
      mapLib={maplibregl}
      style={{ height: "100%" }}
      onClick={handleMouseClick}
      onMouseMove={handleMouseHover}
      hash={true}
      onMoveStart={handleMapMoveStart}
      onMoveEnd={handleMapMoveEnd}
    >
      <Source {...catchmentPartnershipSource}>
        <Layer
          {...catchmentPartnershipLayer}
          paint={{
            "fill-opacity": catchmentPopup
              ? [
                  "case",
                  ["==", ["get", "caba_partn"], catchmentFeature.name],
                  ["number", highlightedOpacity + catchmentOpacityOffset],
                  ["number", opacity + catchmentOpacityOffset],
                ]
              : opacity + catchmentOpacityOffset,
            "fill-color": [
              "get",
              "color",
              [
                "get",
                ["get", "caba_partn"],
                ["literal", catchmentPartnershipProperties],
              ],
            ],
          }}
          filter={getFilterExpressionsFromSourceProperties(
            catchmentPartnershipProperties
          )}
        />
      </Source>
      <Source {...giglOpenSpaceSource}>
        <Layer
          {...giglOpenSpaceLayer}
          paint={{
            "raster-opacity": opacity + giglOpacityOffset,
            "raster-resampling": "nearest",
          }}
          layout={{
            visibility: giglOpenSpaceProperties[
              Object.keys(giglOpenSpaceProperties)[0]
            ].visible
              ? "visible"
              : "none",
          }}
        />
      </Source>
      <Source {...thamesCombinedSource}>
        <Layer
          {...thamesCombinedLayer}
          layout={{
            visibility: thamesCombinedProperties[
              Object.keys(thamesCombinedProperties)[0]
            ].visible
              ? "visible"
              : "none",
          }}
          paint={{
            "fill-color": thamesCombinedColour,
            "fill-opacity": opacity + thamesCombinedOpacityOffset,
          }}
        />
      </Source>
      <Source {...thamesSeperateSource}>
        <Layer
          {...thamesSeparateLayer}
          layout={{
            visibility: thamesSeperateProperties[
              Object.keys(thamesSeperateProperties)[0]
            ].visible
              ? "visible"
              : "none",
          }}
          paint={{
            "fill-color": thamesSeparateColour,
            "fill-opacity": opacity + thamesSeparateOpacityOffset,
          }}
        />
      </Source>
      <Source {...watercoursesSource}>
        <Layer
          {...watercoursesLayer}
          layout={{
            visibility: watercoursesProperties[
              Object.keys(watercoursesProperties)[0]
            ].visible
              ? "visible"
              : "none",
          }}
          paint={{
            "fill-color": watercoursesColour,
            "fill-opacity": opacity + watercoursesOpacityOffset,
          }}
        />
      </Source>
      <Source {...boroughSource}>
        <Layer
          {...boroughLayer}
          layout={{
            visibility: boroughProperties[Object.keys(boroughProperties)[0]]
              .visible
              ? "visible"
              : "none",
          }}
        />
      </Source>
      <Source {...sudsSitesSource}>
        <Layer
          {...sudsSitesLayer}
          filter={filter}
          paint={{
            ...sudsSitesLayer.paint,
            "circle-color": [
              "get",
              "color",
              ["get", ["get", "context"], ["literal", checkedSites]],
            ],
          }}
        />
      </Source>
      {sudPopup &&
        sudVisibleForPopup &&
        (isLargeMobile ? (
          <FullScreenDialog feature={sudFeature} />
        ) : (
          <Popup
            longitude={sudFeature.geometry.coordinates[0]}
            latitude={sudFeature.geometry.coordinates[1]}
            closeButton={false}
            maxWidth={"800px"}
            anchor="top"
          >
            <SudCard feature={sudFeature} />
          </Popup>
        ))}
      {catchmentPopup &&
        catchmentPartnershipProperties[catchmentFeature.name].visible && (
          <Popup
            longitude={catchmentFeature.lngLat.lng}
            latitude={catchmentFeature.lngLat.lat}
            closeButton={false}
            anchor="bottom"
          >
            <CatchmentCard catchmentFeature={catchmentFeature} />
          </Popup>
        )}
      {sudHover && (
        <Popup
          longitude={sudHoverFeature.geometry.coordinates[0]}
          latitude={sudHoverFeature.geometry.coordinates[1]}
          closeButton={false}
          anchor="bottom"
        >
          <SudHoverCard context={sudHoverFeature.properties.context} />
        </Popup>
      )}
      <GeocoderControl position="top-left" />
      <FullscreenControl position="top-left" />
      <GeolocateControl position="top-left" />
      <NavigationControl position="top-left" />
    </Map>
  );
}

const getFilterExpressionsFromSourceProperties = (sourceProperties) => {
  if (Object.keys(sourceProperties).length === 0) {
    return ["boolean", false];
  }
  const featuresToShow = Object.keys(sourceProperties).filter(
    (name) => sourceProperties[name].visible
  );

  const pathOfName =
    sourceProperties[Object.keys(sourceProperties)[0]].pathOfName;

  return ["in", ["get", pathOfName], ["literal", featuresToShow]];
};
